import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SettingsState {
  // Appearance Settings
  theme: 'light' | 'dark' | 'vintage' | 'system';
  accentColor: string;
  primaryColor: string;
  borderRadius: 'none' | 'small' | 'medium' | 'large' | 'full';
  fontSize: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
  fontFamily: 'inter' | 'system' | 'serif' | 'mono';
  reducedMotion: boolean;
  highContrast: boolean;
  compactMode: boolean;
  
  // Display Mode Settings  
  displayMode: 'public' | 'professional' | 'minimal' | 'custom';
  showImages: boolean;
  showThumbnails: boolean;
  showBackgroundElements: boolean;
  showAnimations: boolean;
  
  // Icon Settings
  iconStyle: 'lucide' | 'heroicons' | 'basic' | 'none';
  showCategoryIcons: boolean;
  showStatusIcons: boolean;  
  showNavigationIcons: boolean;
  iconSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  // AI & Smart Features
  enableAISummaries: boolean;
  enableSmartGrouping: boolean;
  enableAutocomplete: boolean;
  enableSuggestions: boolean;
  enablePredictions: boolean;
  enableSmartFilters: boolean;
  aiInsightLevel: 'basic' | 'detailed' | 'comprehensive';
  
  // Data & Performance  
  defaultGroupingMode: 'individual' | 'smart' | 'strict';
  loadingStrategy: 'lazy' | 'eager' | 'progressive';
  cacheEnabled: boolean;
  cacheDuration: number; // in hours
  preloadData: boolean;
  virtualScrolling: boolean;
  batchSize: number;
  
  // View & Layout
  defaultView: 'grid' | 'list' | 'map' | 'newspaper';
  cardsPerRow: 2 | 3 | 4 | 5 | 6;
  showSidebar: boolean;
  sidebarPosition: 'left' | 'right';
  headerStyle: 'fixed' | 'static' | 'overlay';
  footerVisible: boolean;
  breadcrumbsVisible: boolean;
  
  // Map Settings
  mapStyle: 'streets' | 'satellite' | 'terrain' | 'dark' | 'light';
  mapControls: boolean;
  clusterMarkers: boolean;
  showHeatmap: boolean;
  mapZoom: number;
  
  // Filter & Search  
  rememberFilters: boolean;
  autoApplyFilters: boolean;
  searchSuggestions: boolean;
  searchHistory: boolean;
  maxSearchHistory: number;
  showRecentSearches: boolean;
  
  // Notifications & Alerts
  notifications: boolean;
  newApplicationAlerts: boolean;
  statusChangeAlerts: boolean;
  favoriteAlerts: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
  notificationSound: boolean;
  
  // Privacy & Data
  analyticsEnabled: boolean;
  crashReporting: boolean;
  usageTracking: boolean;
  dataSharing: boolean;
  cookiesEnabled: boolean;
  
  // Accessibility
  screenReader: boolean;  
  keyboardNavigation: boolean;
  focusIndicators: boolean;
  skipLinks: boolean;
  colorBlindMode: 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia';
  
  // Advanced/Developer
  debugMode: boolean;
  showPerformanceMetrics: boolean;
  devTools: boolean;
  experimentalFeatures: boolean;
  apiEndpoint: string;
  
  // Custom User Profiles
  profileName: string;
  customProfiles: Array<{
    id: string;
    name: string;
    settings: Partial<SettingsState>;
  }>;
}

interface SettingsActions {
  updateSetting: <K extends keyof SettingsState>(key: K, value: SettingsState[K]) => void;
  updateMultipleSettings: (settings: Partial<SettingsState>) => void;
  resetSettings: () => void;
  resetCategory: (category: 'appearance' | 'display' | 'icons' | 'ai' | 'data' | 'view' | 'map' | 'filter' | 'notifications' | 'privacy' | 'accessibility' | 'advanced') => void;
  exportSettings: () => string;
  importSettings: (settingsJson: string) => void;
  saveProfile: (name: string) => void;
  loadProfile: (profileId: string) => void;
  deleteProfile: (profileId: string) => void;
  getDisplayModePreset: (mode: 'public' | 'professional' | 'minimal') => Partial<SettingsState>;
  applyDisplayModePreset: (mode: 'public' | 'professional' | 'minimal') => void;
}

const defaultSettings: SettingsState = {
  // Appearance
  theme: 'system',
  accentColor: '#8b5cf6',
  primaryColor: '#6366f1', 
  borderRadius: 'medium',
  fontSize: 'base',
  fontFamily: 'inter',
  reducedMotion: false,
  highContrast: false,
  compactMode: false,
  
  // Display Mode
  displayMode: 'public',
  showImages: true,
  showThumbnails: true,
  showBackgroundElements: true,
  showAnimations: true,
  
  // Icons
  iconStyle: 'lucide',
  showCategoryIcons: true,
  showStatusIcons: true,
  showNavigationIcons: true,
  iconSize: 'md',
  
  // AI & Smart Features
  enableAISummaries: true,
  enableSmartGrouping: true,
  enableAutocomplete: true,
  enableSuggestions: true,
  enablePredictions: false,
  enableSmartFilters: true,
  aiInsightLevel: 'detailed',
  
  // Data & Performance
  defaultGroupingMode: 'smart',
  loadingStrategy: 'lazy',
  cacheEnabled: true,
  cacheDuration: 6,
  preloadData: false,
  virtualScrolling: true,
  batchSize: 50,
  
  // View & Layout
  defaultView: 'grid',
  cardsPerRow: 3,
  showSidebar: true,
  sidebarPosition: 'right',
  headerStyle: 'fixed',
  footerVisible: true,
  breadcrumbsVisible: true,
  
  // Map
  mapStyle: 'streets',
  mapControls: true,
  clusterMarkers: true,
  showHeatmap: false,
  mapZoom: 12,
  
  // Filter & Search
  rememberFilters: true,
  autoApplyFilters: true,
  searchSuggestions: true,
  searchHistory: true,
  maxSearchHistory: 50,
  showRecentSearches: true,
  
  // Notifications
  notifications: true,
  newApplicationAlerts: true,
  statusChangeAlerts: true,
  favoriteAlerts: true,
  emailNotifications: false,
  pushNotifications: false,
  notificationSound: true,
  
  // Privacy
  analyticsEnabled: true,
  crashReporting: true,
  usageTracking: true,
  dataSharing: false,
  cookiesEnabled: true,
  
  // Accessibility
  screenReader: false,
  keyboardNavigation: true,
  focusIndicators: true,
  skipLinks: false,
  colorBlindMode: 'none',
  
  // Advanced
  debugMode: false,
  showPerformanceMetrics: false,
  devTools: false,
  experimentalFeatures: false,
  apiEndpoint: 'https://api.city-scope.co.uk',
  
  // Profiles
  profileName: 'Default',
  customProfiles: [],
};

export const useSettingsStore = create<SettingsState & SettingsActions>()(
  persist(
    (set, get) => ({
      ...defaultSettings,
      
      updateSetting: (key, value) => {
        set((state) => ({ ...state, [key]: value }));
      },
      
      updateMultipleSettings: (settings) => {
        set((state) => ({ ...state, ...settings }));
      },
      
      resetSettings: () => {
        set(defaultSettings);
      },
      
      resetCategory: (category) => {
        const categoryDefaults = getCategoryDefaults(category);
        set((state) => ({ ...state, ...categoryDefaults }));
      },
      
      exportSettings: () => {
        const state = get();
        const settings = Object.fromEntries(
          Object.entries(state).filter(([, value]) => typeof value !== 'function')
        );
        return JSON.stringify(settings, null, 2);
      },
      
      importSettings: (settingsJson) => {
        try {
          const importedSettings = JSON.parse(settingsJson);
          set((state) => ({ ...state, ...importedSettings }));
        } catch (error) {
          console.error('Failed to import settings:', error);
        }
      },
      
      saveProfile: (name) => {
        const state = get();
        const settings = Object.fromEntries(
          Object.entries(state).filter(([key, value]) => key !== 'customProfiles' && typeof value !== 'function')
        );
        
        const newProfile = {
          id: `profile_${Date.now()}`,
          name,
          settings
        };
        
        set((state) => ({
          ...state,
          customProfiles: [...state.customProfiles, newProfile],
          profileName: name
        }));
      },
      
      loadProfile: (profileId) => {
        const state = get();
        const profile = state.customProfiles.find(p => p.id === profileId);
        if (profile) {
          set((state) => ({ ...state, ...profile.settings, profileName: profile.name }));
        }
      },
      
      deleteProfile: (profileId) => {
        set((state) => ({
          ...state,
          customProfiles: state.customProfiles.filter(p => p.id !== profileId)
        }));
      },
      
      getDisplayModePreset: (mode) => {
        const presets = {
          public: {
            showImages: true,
            showThumbnails: true,
            showBackgroundElements: true,
            showAnimations: true,
            showCategoryIcons: true,
            iconStyle: 'lucide' as const,
            enableAISummaries: true,
            theme: 'light' as const,
            compactMode: false,
          },
          professional: {
            showImages: false,
            showThumbnails: false, 
            showBackgroundElements: false,
            showAnimations: false,
            showCategoryIcons: false,
            iconStyle: 'basic' as const,
            enableAISummaries: false,
            theme: 'light' as const,
            compactMode: true,
          },
          minimal: {
            showImages: false,
            showThumbnails: false,
            showBackgroundElements: false,
            showAnimations: false,
            showCategoryIcons: false,
            iconStyle: 'none' as const,
            enableAISummaries: false,
            theme: 'light' as const,
            compactMode: true,
          }
        };
        return presets[mode];
      },
      
      applyDisplayModePreset: (mode) => {
        const preset = get().getDisplayModePreset(mode);
        set((state) => ({ ...state, ...preset, displayMode: mode }));
      },
    }),
    {
      name: 'city-scope-settings',
      version: 1,
    }
  )
);

function getCategoryDefaults(category: string): Partial<SettingsState> {
  const categoryMappings = {
    appearance: {
      theme: 'system',
      accentColor: '#8b5cf6',
      primaryColor: '#6366f1',
      borderRadius: 'medium',
      fontSize: 'base',
      fontFamily: 'inter',
      reducedMotion: false,
      highContrast: false,
      compactMode: false,
    },
    display: {
      displayMode: 'public',
      showImages: true,
      showThumbnails: true,
      showBackgroundElements: true,
      showAnimations: true,
    },
    icons: {
      iconStyle: 'lucide',
      showCategoryIcons: true,
      showStatusIcons: true,
      showNavigationIcons: true,
      iconSize: 'md',
    },
    ai: {
      enableAISummaries: true,
      enableSmartGrouping: true,
      enableAutocomplete: true,
      enableSuggestions: true,
      enablePredictions: false,
      enableSmartFilters: true,
      aiInsightLevel: 'detailed',
    },
    data: {
      defaultGroupingMode: 'smart',
      loadingStrategy: 'lazy',
      cacheEnabled: true,
      cacheDuration: 6,
      preloadData: false,
      virtualScrolling: true,
      batchSize: 50,
    },
    view: {
      defaultView: 'grid',
      cardsPerRow: 3,
      showSidebar: true,
      sidebarPosition: 'right',
      headerStyle: 'fixed',
      footerVisible: true,
      breadcrumbsVisible: true,
    },
    map: {
      mapStyle: 'streets',
      mapControls: true,
      clusterMarkers: true,
      showHeatmap: false,
      mapZoom: 12,
    },
    filter: {
      rememberFilters: true,
      autoApplyFilters: true,
      searchSuggestions: true,
      searchHistory: true,
      maxSearchHistory: 50,
      showRecentSearches: true,
    },
    notifications: {
      notifications: true,
      newApplicationAlerts: true,
      statusChangeAlerts: true,
      favoriteAlerts: true,
      emailNotifications: false,
      pushNotifications: false,
      notificationSound: true,
    },
    privacy: {
      analyticsEnabled: true,
      crashReporting: true,
      usageTracking: true,
      dataSharing: false,
      cookiesEnabled: true,
    },
    accessibility: {
      screenReader: false,
      keyboardNavigation: true,
      focusIndicators: true,
      skipLinks: false,
      colorBlindMode: 'none',
    },
    advanced: {
      debugMode: false,
      showPerformanceMetrics: false,
      devTools: false,
      experimentalFeatures: false,
      apiEndpoint: 'https://api.city-scope.co.uk',
    }
  };
  
  return categoryMappings[category] || {};
} 
